import { ReactElement, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCopyWorksheetFromToken } from 'hooks/api/backend/worksheets/useWorksheets';
import { useNovaToast } from 'hooks/utils/useNovaToast';
import { ToastVariant } from 'types/Toast';
import { ProtectedRoutes } from 'types/Routes';
import { PageSpinner } from 'components/atoms/PageSpinner';
import { useUser } from 'utils/auth';
import { useIsTeacherOrAdmin } from 'hooks/utils/useIsTeacherOrAdmin';

export const WorksheetSharePage = (): ReactElement => {
  const { token } = useParams<{ token: string }>();
  const { data: user } = useUser();
  const navigate = useNavigate();
  const { mutateAsync: copyWorksheet } = useCopyWorksheetFromToken();
  const { show: showToast } = useNovaToast();
  const hasExecuted = useRef(false);
  const isTeacherOrAdmin = useIsTeacherOrAdmin();

  useEffect(() => {
    if (hasExecuted.current) return; // Prevent multiple executions

    const handleCopyWorksheet = async (): Promise<void> => {
      if (!isTeacherOrAdmin) {
        showToast({
          title: 'Du bist nicht berechtigt, dieses Arbeitsblatt zu kopieren',
          variant: ToastVariant.ERROR,
        });
        navigate(ProtectedRoutes.HOME);
        return;
      }

      if (!token) {
        showToast({
          title: 'Ungültiger Link',
          variant: ToastVariant.ERROR,
        });
        navigate(ProtectedRoutes.WORKSHEET_DASHBOARD);
        return;
      }

      try {
        const newWorksheet = await copyWorksheet(token);
        showToast({
          title: 'Arbeitsblatt erfolgreich kopiert!',
          variant: ToastVariant.SUCCESS,
        });
        navigate(`${ProtectedRoutes.WORKSHEET_DETAIL}/${newWorksheet.id}`);
      } catch (error) {
        showToast({
          title: 'Fehler beim Kopieren des Arbeitsblatts',
          variant: ToastVariant.ERROR,
        });
        navigate(ProtectedRoutes.WORKSHEET_DASHBOARD);
      }
    };

    hasExecuted.current = true;
    handleCopyWorksheet();
  }, [token, copyWorksheet, navigate, showToast, user, isTeacherOrAdmin]);

  return <PageSpinner />;
};
